import { useState, useEffect } from 'react';
import Button from './Button';
import Input from './Input';
import Select from './Select';

export interface SearchField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'dateRange' | 'boolean';
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
}

export interface SearchCondition {
  field: string;
  operator: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'startsWith' | 'endsWith' | 'between';
  value: string;
  value2?: string; // For 'between' operator
}

interface AdvancedSearchProps {
  fields: SearchField[];
  onSearch: (conditions: SearchCondition[]) => void;
  onReset?: () => void;
  className?: string;
  quickSearchPlaceholder?: string;
  quickSearchField?: string;
  initialConditions?: SearchCondition[];
}

const getOperatorsForType = (type: string): Array<{ value: string; label: string }> => {
  switch (type) {
    case 'text':
      return [
        { value: 'contains', label: '含む' },
        { value: 'eq', label: '等しい' },
        { value: 'startsWith', label: 'から始まる' },
        { value: 'endsWith', label: 'で終わる' },
      ];
    case 'number':
      return [
        { value: 'eq', label: '等しい' },
        { value: 'ne', label: '等しくない' },
        { value: 'gt', label: 'より大きい' },
        { value: 'gte', label: '以上' },
        { value: 'lt', label: 'より小さい' },
        { value: 'lte', label: '以下' },
        { value: 'between', label: '範囲' },
      ];
    case 'date':
    case 'dateRange':
      return [
        { value: 'eq', label: '等しい' },
        { value: 'gt', label: 'より後' },
        { value: 'gte', label: '以降' },
        { value: 'lt', label: 'より前' },
        { value: 'lte', label: '以前' },
        { value: 'between', label: '期間' },
      ];
    case 'select':
      return [
        { value: 'eq', label: '等しい' },
        { value: 'ne', label: '等しくない' },
      ];
    case 'boolean':
      return [
        { value: 'eq', label: '等しい' },
      ];
    default:
      return [{ value: 'eq', label: '等しい' }];
  }
};

export default function AdvancedSearch({ 
  fields, 
  onSearch, 
  onReset, 
  className = '',
  quickSearchPlaceholder = '検索...',
  quickSearchField,
  initialConditions = [],
}: AdvancedSearchProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [conditions, setConditions] = useState<SearchCondition[]>(initialConditions);
  const [quickSearch, setQuickSearch] = useState(() => {
    if (initialConditions.length === 1) {
      const c = initialConditions[0];
      const targetKey = quickSearchField || fields.find(f => f.type === 'text')?.key;
      if (c.field === targetKey && c.operator === 'contains') {
        return c.value;
      }
    }
    return '';
  });

  // initialConditionsが変更されたときに条件を更新
  useEffect(() => {
    if (initialConditions.length > 0) {
      setConditions(initialConditions);
    }
  }, [initialConditions]);

  const handleAddCondition = () => {
    const firstField = fields[0];
    if (!firstField) return;

    const operators = getOperatorsForType(firstField.type);
    setConditions(prev => [...prev, {
      field: firstField.key,
      operator: operators[0].value as SearchCondition['operator'],
      value: '',
    }]);
  };

  const handleRemoveCondition = (index: number) => {
    setConditions(prev => prev.filter((_, i) => i !== index));
  };

  const handleConditionChange = (index: number, key: keyof SearchCondition, value: string) => {
    setConditions(prev => {
      const newConditions = [...prev];
      newConditions[index] = { ...newConditions[index], [key]: value };
      
      // フィールドが変更された場合、オペレーターをリセット
      if (key === 'field') {
        const field = fields.find(f => f.key === value);
        if (field) {
          const operators = getOperatorsForType(field.type);
          newConditions[index].operator = operators[0].value as SearchCondition['operator'];
          newConditions[index].value = '';
          newConditions[index].value2 = undefined;
        }
      }
      
      return newConditions;
    });
  };

  const handleSearch = () => {
    const validConditions = conditions.filter(c => c.value.trim() !== '');
    onSearch(validConditions);
  };

  const handleQuickSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (quickSearch.trim()) {
      // クイック検索は指定されたフィールドまたは最初のテキストフィールドで「含む」検索
      const searchField = quickSearchField 
        ? fields.find(f => f.key === quickSearchField)
        : fields.find(f => f.type === 'text');
      if (searchField) {
        onSearch([{ field: searchField.key, operator: 'contains', value: quickSearch }]);
      }
    } else {
      onSearch([]);
    }
  };

  const handleReset = () => {
    setConditions([]);
    setQuickSearch('');
    onSearch([]);
    if (onReset) onReset();
  };

  const getFieldByKey = (key: string) => fields.find(f => f.key === key);

  const activeConditionsCount = conditions.filter(c => c.value.trim() !== '').length;

  return (
    <div className={`space-y-3 ${className}`}>
      {/* 検索バー */}
      <div className="flex flex-col sm:flex-row gap-2">
        {/* クイック検索 */}
        <form onSubmit={handleQuickSearch} className="flex-1 flex flex-col sm:flex-row gap-2 min-w-0">
          <div className="relative flex-1">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              placeholder={quickSearchPlaceholder}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
          <Button type="submit" variant="secondary" className="w-full sm:w-auto">
            検索
          </Button>
        </form>

        {/* 詳細検索ボタン */}
        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            variant="secondary"
            onClick={() => setIsExpanded(!isExpanded)}
            className="relative whitespace-nowrap flex-1 sm:flex-none"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            詳細検索
            <svg 
              className={`w-4 h-4 ml-1 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            {activeConditionsCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                {activeConditionsCount}
              </span>
            )}
          </Button>
          
          {(activeConditionsCount > 0 || quickSearch) && (
            <Button variant="ghost" onClick={handleReset} title="リセット" className="shrink-0">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          )}
        </div>
      </div>

      {/* 展開可能な詳細検索パネル */}
      <div 
        className={`transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          {/* 条件一覧（スクロール可能エリア） */}
          <div className="max-h-[280px] overflow-y-auto p-3 space-y-2">
            {conditions.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                <svg className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="text-sm mb-2">検索条件がありません</p>
                <Button variant="secondary" size="sm" onClick={handleAddCondition}>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  条件を追加
                </Button>
              </div>
            ) : (
              <>
              {/* 列ヘッダー */}
              <div className="hidden sm:flex items-center gap-2 px-2 pb-1">
                <span className="text-xs font-medium text-gray-400 px-2 py-1 flex-shrink-0">&nbsp;</span>
                <span className="w-28 text-xs font-medium text-gray-500 dark:text-gray-400">項目</span>
                <span className="w-24 text-xs font-medium text-gray-500 dark:text-gray-400">条件</span>
                <span className="flex-1 min-w-[100px] max-w-[200px] text-xs font-medium text-gray-500 dark:text-gray-400">値</span>
              </div>
              {conditions.map((condition, index) => {
                const field = getFieldByKey(condition.field);
                const operators = field ? getOperatorsForType(field.type) : [];
                const showSecondValue = condition.operator === 'between';

                return (
                  <div 
                    key={index} 
                    className="flex flex-wrap sm:flex-nowrap items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    {/* AND表示（2番目以降） */}
                    {index > 0 ? (
                      <span className="text-xs font-medium text-blue-500 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded flex-shrink-0">
                        AND
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-gray-400 px-2 py-1 flex-shrink-0">
                        条件
                      </span>
                    )}
                    
                    {/* フィールド選択 */}
                    <select
                      value={condition.field}
                      onChange={(e) => handleConditionChange(index, 'field', e.target.value)}
                      className="w-full sm:w-28 px-2 py-1.5 h-[34px] text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {fields.map(f => (
                        <option key={f.key} value={f.key}>{f.label}</option>
                      ))}
                    </select>

                    {/* オペレーター選択 */}
                    <select
                      value={condition.operator}
                      onChange={(e) => handleConditionChange(index, 'operator', e.target.value)}
                      className="w-full sm:w-24 px-2 py-1.5 h-[34px] text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {operators.map(op => (
                        <option key={op.value} value={op.value}>{op.label}</option>
                      ))}
                    </select>

                    {/* 値入力 */}
                    {field?.type === 'select' && field.options ? (
                      <select
                        value={condition.value}
                        onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                        className="w-full sm:flex-1 sm:min-w-[100px] sm:max-w-[200px] px-2 py-1.5 h-[34px] text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">選択...</option>
                        {field.options.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : field?.type === 'boolean' ? (
                      <select
                        value={condition.value}
                        onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                        className="w-full sm:w-20 px-2 py-1.5 h-[34px] text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">選択...</option>
                        <option value="true">はい</option>
                        <option value="false">いいえ</option>
                      </select>
                    ) : (
                      <input
                        type={field?.type === 'date' || field?.type === 'dateRange' ? 'date' : field?.type === 'number' ? 'number' : 'text'}
                        value={condition.value}
                        onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                        placeholder={field?.placeholder || '値を入力'}
                        className="w-full sm:flex-1 sm:min-w-[100px] sm:max-w-[200px] px-2 py-1.5 h-[34px] text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    )}

                    {/* 範囲検索の2つ目の値 */}
                    {showSecondValue && (
                      <>
                        <span className="text-gray-400 text-sm flex-shrink-0 hidden sm:inline">〜</span>
                        <input
                          type={field?.type === 'date' || field?.type === 'dateRange' ? 'date' : field?.type === 'number' ? 'number' : 'text'}
                          value={condition.value2 || ''}
                          onChange={(e) => handleConditionChange(index, 'value2', e.target.value)}
                          className="w-full sm:flex-1 sm:min-w-[100px] sm:max-w-[200px] px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </>
                    )}

                    {/* 削除ボタン */}
                    <button
                      onClick={() => handleRemoveCondition(index)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors flex-shrink-0"
                      title="条件を削除"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                );
              })}
              </>
            )}
          </div>

          {/* アクションボタン（固定フッター） */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/80 rounded-b-lg">
            <Button variant="secondary" size="sm" onClick={handleAddCondition}>
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="hidden sm:inline">条件を</span>追加
            </Button>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={handleReset}>
                リセット
              </Button>
              <Button size="sm" onClick={handleSearch}>
                <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="hidden sm:inline">検索実行</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* アクティブな検索条件の表示（折りたたみ時） */}
      {!isExpanded && activeConditionsCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {conditions.filter(c => c.value.trim() !== '').map((condition, index) => {
            const field = getFieldByKey(condition.field);
            const operatorLabel = getOperatorsForType(field?.type || 'text').find(o => o.value === condition.operator)?.label || '';
            let valueLabel = condition.value;
            
            if (field?.type === 'select' && field.options) {
              valueLabel = field.options.find(o => o.value === condition.value)?.label || condition.value;
            } else if (field?.type === 'boolean') {
              valueLabel = condition.value === 'true' ? 'はい' : 'いいえ';
            }
            
            return (
              <span 
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full"
              >
                <span className="font-medium">{field?.label}</span>
                <span className="text-blue-600 dark:text-blue-400">{operatorLabel}</span>
                <span>{valueLabel}</span>
                {condition.operator === 'between' && condition.value2 && (
                  <span>〜 {condition.value2}</span>
                )}
                <button
                  onClick={() => {
                    const newConditions = conditions.filter((_, i) => i !== index);
                    setConditions(newConditions);
                    onSearch(newConditions.filter(c => c.value.trim() !== ''));
                  }}
                  className="ml-1 hover:text-blue-600"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// 検索条件をURLクエリパラメータに変換するユーティリティ
export function conditionsToQueryParams(conditions: SearchCondition[]): string {
  if (conditions.length === 0) return '';
  return encodeURIComponent(JSON.stringify(conditions));
}

// URLクエリパラメータから検索条件を復元するユーティリティ
export function queryParamsToConditions(params: string): SearchCondition[] {
  if (!params) return [];
  try {
    return JSON.parse(decodeURIComponent(params));
  } catch {
    return [];
  }
}
